import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { projectApi, taskApi, columnApi, userApi, departmentApi, sectorApi, flagsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  ListTodo,
  LayoutGrid,
  Table as TableIcon,
  Calendar,
  Search,
  Filter,
  CheckCircle2,
  Circle,
  Clock,
  AlertCircle,
  Loader2,
  ChevronRight,
  User,
  FolderKanban,
  Building,
  Layers,
  Plus,
  UserCircle,
  Trash2,
  Users,
  CheckSquare,
  Square,
  X,
  Flag,
  PlayCircle,
  Eye,
  RotateCcw,
  RefreshCcw,
} from 'lucide-react';
import { format, isToday, isTomorrow, isPast, addDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';

const TasksPage = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [personalTasks, setPersonalTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [users, setUsers] = useState([]);
  const [projectColumns, setProjectColumns] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [projectFilter, setProjectFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('list');
  const [showNewTaskDialog, setShowNewTaskDialog] = useState(false);
  const [showTaskDetailDialog, setShowTaskDetailDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [newTask, setNewTask] = useState({ 
    title: '', 
    description: '', 
    due_date: '', 
    priority: 'medium',
    project_id: '',
    column_id: '',
    assigned_users: [],
    checklist: [],
    recorrencia: null,
    recorrencia_config: null
  });
  const [newSubtask, setNewSubtask] = useState('');
  const [savingTask, setSavingTask] = useState(false);
  const [loadingColumns, setLoadingColumns] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [projectsRes, personalTasksRes, usersRes, deptsRes, sectorsRes] = await Promise.all([
        projectApi.list(),
        taskApi.listPersonal(),
        userApi.listAssignable().catch(() => ({ data: [] })),
        departmentApi.list().catch(() => ({ data: [] })),
        sectorApi.list().catch(() => ({ data: [] })),
      ]);
      
      const projectsList = projectsRes.data;
      setProjects(projectsList);
      setUsers(usersRes.data || []);
      setDepartments(deptsRes.data || []);
      setSectors(sectorsRes.data || []);
      setPersonalTasks(personalTasksRes.data.map(t => ({
        ...t,
        project_name: 'Pessoal',
        is_personal: true
      })));
      
      // Carregar tarefas de todos os projetos em paralelo
      const taskPromises = projectsList.map(project => 
        taskApi.list(project.id)
          .then(tasksRes => tasksRes.data.map(t => ({
            ...t,
            project_name: project.name,
            project_id: project.id,
            is_personal: false
          })))
          .catch(() => []) // Projeto pode não ter tarefas
      );
      
      const tasksArrays = await Promise.all(taskPromises);
      const allTasks = tasksArrays.flat();
      
      setTasks(allTasks);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar tarefas');
    } finally {
      setLoading(false);
    }
  };

  const loadProjectColumns = async (projectId) => {
    if (!projectId || projectColumns[projectId]) return;
    
    setLoadingColumns(true);
    try {
      const res = await columnApi.list(projectId);
      setProjectColumns(prev => ({ ...prev, [projectId]: res.data }));
      // Auto-select first column
      if (res.data.length > 0) {
        setNewTask(prev => ({ ...prev, column_id: res.data[0].id }));
      }
    } catch (error) {
      console.error('Error loading columns:', error);
    } finally {
      setLoadingColumns(false);
    }
  };

  const handleProjectChange = async (projectId) => {
    const isPersonal = projectId === 'personal';
    setNewTask(prev => ({ ...prev, project_id: isPersonal ? '' : projectId, column_id: '' }));
    if (!isPersonal && projectId) {
      await loadProjectColumns(projectId);
    }
  };

  const handleCreateTask = async () => {
    if (!newTask.title.trim()) {
      toast.error('Título é obrigatório');
      return;
    }
    
    setSavingTask(true);
    try {
      if (newTask.project_id && newTask.project_id !== 'personal') {
        // Criar tarefa em um projeto
        if (!newTask.column_id) {
          toast.error('Selecione uma coluna');
          setSavingTask(false);
          return;
        }
        await taskApi.create({
          title: newTask.title,
          description: newTask.description,
          due_date: newTask.due_date || null,
          priority: newTask.priority,
          project_id: newTask.project_id,
          column_id: newTask.column_id,
          assigned_users: newTask.assigned_users,
          checklist: newTask.checklist,
          recorrencia: newTask.recorrencia,
          recorrencia_config: newTask.recorrencia_config
        });
        toast.success('Tarefa criada no projeto!');
      } else {
        // Criar tarefa pessoal
        await taskApi.createPersonal({
          title: newTask.title,
          description: newTask.description,
          due_date: newTask.due_date || null,
          priority: newTask.priority,
          checklist: newTask.checklist,
          recorrencia: newTask.recorrencia,
          recorrencia_config: newTask.recorrencia_config
        });
        toast.success('Tarefa pessoal criada!');
      }
      setShowNewTaskDialog(false);
      setNewTask({ title: '', description: '', due_date: '', priority: 'medium', project_id: '', column_id: '', assigned_users: [], checklist: [], recorrencia: null, recorrencia_config: null });
      setNewSubtask('');
      loadData();
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error('Erro ao criar tarefa');
    } finally {
      setSavingTask(false);
    }
  };

  const handleAddSubtask = () => {
    if (!newSubtask.trim()) return;
    setNewTask(prev => ({
      ...prev,
      checklist: [...prev.checklist, { id: Date.now().toString(), text: newSubtask.trim(), completed: false }]
    }));
    setNewSubtask('');
  };

  const handleRemoveSubtask = (subtaskId) => {
    setNewTask(prev => ({
      ...prev,
      checklist: prev.checklist.filter(s => s.id !== subtaskId)
    }));
  };

  const toggleAssignedUser = (userId) => {
    setNewTask(prev => ({
      ...prev,
      assigned_users: prev.assigned_users.includes(userId)
        ? prev.assigned_users.filter(id => id !== userId)
        : [...prev.assigned_users, userId]
    }));
  };

  const handleCreatePersonalTask = async () => {
    if (!newTask.title.trim()) {
      toast.error('Título é obrigatório');
      return;
    }
    
    setSavingTask(true);
    try {
      await taskApi.createPersonal({
        title: newTask.title,
        description: newTask.description,
        due_date: newTask.due_date || null,
        priority: newTask.priority,
        checklist: newTask.checklist || [],
        assigned_users: newTask.assigned_users || []
      });
      toast.success('Tarefa pessoal criada!');
      setShowNewTaskDialog(false);
      setNewTask({ title: '', description: '', due_date: '', priority: 'medium', project_id: '', column_id: '', assigned_users: [], checklist: [] });
      loadData();
    } catch (error) {
      console.error('Error creating personal task:', error);
      toast.error('Erro ao criar tarefa');
    } finally {
      setSavingTask(false);
    }
  };

  const handleDeletePersonalTask = async (taskId, e) => {
    e.stopPropagation();
    try {
      await taskApi.delete(taskId);
      toast.success('Tarefa excluída');
      loadData();
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Erro ao excluir tarefa');
    }
  };

  const handleOpenTaskDetail = (task, e) => {
    if (e) e.stopPropagation();
    setSelectedTask(task);
    setShowTaskDetailDialog(true);
  };

  const handleTogglePersonalTaskComplete = async (task, e) => {
    e.stopPropagation();
    try {
      const newStatus = task.completed_date ? null : new Date().toISOString();
      await taskApi.update(task.id, { completed_date: newStatus });
      toast.success(newStatus ? 'Tarefa concluída!' : 'Tarefa reaberta');
      loadData();
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Erro ao atualizar tarefa');
    }
  };

  const handleUpdateTaskFlag = async (taskId, flag, e) => {
    if (e) e.stopPropagation();
    try {
      await flagsApi.updateTaskFlag(taskId, flag);
      const flagLabels = {
        'proxima_acao': 'Próxima Ação',
        'aguardando_retorno': 'Aguardando Retorno',
        'revisar': 'Revisar'
      };
      toast.success(flag ? `Flag definida: ${flagLabels[flag]}` : 'Flag removida');
      loadData();
    } catch (error) {
      console.error('Error updating flag:', error);
      toast.error('Erro ao atualizar flag');
    }
  };

  const getFlagInfo = (flag) => {
    const flags = {
      'proxima_acao': { label: 'Próxima Ação', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', icon: PlayCircle },
      'aguardando_retorno': { label: 'Aguardando', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', icon: Clock },
      'revisar': { label: 'Revisar', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', icon: Eye },
    };
    return flags[flag] || null;
  };

  const getStatusInfo = (status) => {
    const statuses = {
      'todo': { label: 'A Fazer', color: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300', icon: Circle },
      'doing': { label: 'Fazendo', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300', icon: Clock },
      'review': { label: 'Revisão', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300', icon: AlertCircle },
      'done': { label: 'Concluído', color: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300', icon: CheckCircle2 },
    };
    return statuses[status] || statuses['todo'];
  };

  const getPriorityInfo = (priority) => {
    const priorities = {
      'low': { label: 'Baixa', color: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300' },
      'medium': { label: 'Média', color: 'bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300' },
      'high': { label: 'Alta', color: 'bg-orange-100 text-orange-600 dark:bg-orange-900/40 dark:text-orange-300' },
      'urgent': { label: 'Urgente', color: 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300' },
    };
    return priorities[priority] || priorities['medium'];
  };

  const getDueDateInfo = (dueDate) => {
    if (!dueDate) return null;
    const date = new Date(dueDate);
    
    if (isPast(date) && !isToday(date)) {
      return { label: 'Atrasada', color: 'text-red-600 bg-red-50 dark:text-red-400 dark:bg-red-900/30' };
    }
    if (isToday(date)) {
      return { label: 'Hoje', color: 'text-amber-600 bg-amber-50 dark:text-amber-400 dark:bg-amber-900/30' };
    }
    if (isTomorrow(date)) {
      return { label: 'Amanhã', color: 'text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-900/30' };
    }
    return { label: format(date, 'dd/MM'), color: 'text-slate-600 bg-slate-50 dark:text-slate-300 dark:bg-slate-700' };
  };

  // Combine project tasks with personal tasks based on filter
  const allTasksCombined = projectFilter === 'personal' 
    ? personalTasks 
    : projectFilter === 'all' 
      ? [...tasks, ...personalTasks]
      : tasks;

  const filteredTasks = allTasksCombined.filter(task => {
    // Busca
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      if (!task.title?.toLowerCase().includes(search) && 
          !task.description?.toLowerCase().includes(search)) {
        return false;
      }
    }
    
    // Filtro de projeto (já aplicado acima para "personal")
    if (projectFilter !== 'all' && projectFilter !== 'personal' && task.project_id !== projectFilter) {
      return false;
    }
    
    // Filtro de status
    if (statusFilter !== 'all') {
      if (statusFilter === 'done' && !task.completed_date) return false;
      if (statusFilter !== 'done' && task.status !== statusFilter) return false;
    }
    
    return true;
  });

  // Ordenar tarefas por data (mais próximas primeiro, sem data por último)
  const sortedTasks = [...filteredTasks].sort((a, b) => {
    // Tarefas sem data vão para o final
    if (!a.due_date && !b.due_date) return 0;
    if (!a.due_date) return 1;
    if (!b.due_date) return -1;
    
    // Ordenar por data crescente (mais próximas primeiro)
    return new Date(a.due_date) - new Date(b.due_date);
  });

  // Agrupar por status para visão Kanban simplificada
  const tasksByStatus = {
    todo: sortedTasks.filter(t => t.status === 'todo' || !t.status),
    doing: sortedTasks.filter(t => t.status === 'doing'),
    review: sortedTasks.filter(t => t.status === 'review'),
    done: sortedTasks.filter(t => t.status === 'done'),
  };

  // Agrupar por data para Timeline
  const tasksByDate = sortedTasks.reduce((acc, task) => {
    const date = task.due_date ? format(new Date(task.due_date), 'yyyy-MM-dd') : 'sem-data';
    if (!acc[date]) acc[date] = [];
    acc[date].push(task);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div data-testid="tasks-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <ListTodo className="w-7 h-7 text-blue-600 dark:text-blue-400" />
            Todas as Tarefas
          </h2>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            {filteredTasks.length} tarefa(s) encontrada(s) • {personalTasks.length} pessoal(is)
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* New Task Button */}
          <Dialog open={showNewTaskDialog} onOpenChange={setShowNewTaskDialog}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="new-task-btn">
                <Plus className="w-4 h-4" />
                Nova Tarefa
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg dark:bg-slate-800 dark:border-slate-700">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 dark:text-slate-100">
                  <Plus className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  Nova Tarefa
                </DialogTitle>
                <DialogDescription className="dark:text-slate-400">
                  Crie uma tarefa pessoal ou vincule a um projeto existente.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <label className="text-sm font-medium dark:text-slate-300">Título *</label>
                  <Input
                    placeholder="Ex: Revisar relatório"
                    value={newTask.title}
                    onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                    data-testid="task-title-input"
                    className="dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100"
                  />
                </div>
                
                {/* Project Selection */}
                <div>
                  <label className="text-sm font-medium dark:text-slate-300">Projeto (opcional)</label>
                  <Select 
                    value={newTask.project_id} 
                    onValueChange={handleProjectChange}
                  >
                    <SelectTrigger className="dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100">
                      <SelectValue placeholder="Tarefa pessoal (sem projeto)" />
                    </SelectTrigger>
                    <SelectContent className="dark:bg-slate-800 dark:border-slate-700 max-h-80">
                      <SelectItem value="personal" className="dark:text-slate-200 dark:focus:bg-slate-700">
                        <span className="flex items-center gap-2">
                          <UserCircle className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                          Tarefa Pessoal
                        </span>
                      </SelectItem>
                      
                      {/* Agrupar projetos por departamento/setor */}
                      {departments.map(dept => {
                        const deptProjects = projects.filter(p => p.department_id === dept.id);
                        if (deptProjects.length === 0) return null;
                        
                        // Agrupar por setor dentro do departamento
                        const projectsBySector = {};
                        const projectsWithoutSector = [];
                        
                        deptProjects.forEach(p => {
                          if (p.sector_id) {
                            if (!projectsBySector[p.sector_id]) {
                              projectsBySector[p.sector_id] = [];
                            }
                            projectsBySector[p.sector_id].push(p);
                          } else {
                            projectsWithoutSector.push(p);
                          }
                        });
                        
                        return (
                          <SelectGroup key={dept.id}>
                            <SelectLabel className="flex items-center gap-2 text-xs font-semibold text-slate-500 dark:text-slate-400 py-2">
                              <Building className="w-3.5 h-3.5" />
                              {dept.name}
                            </SelectLabel>
                            
                            {/* Projetos sem setor */}
                            {projectsWithoutSector.map(p => (
                              <SelectItem key={p.id} value={p.id} className="dark:text-slate-200 dark:focus:bg-slate-700 pl-6">
                                <span className="flex items-center gap-2">
                                  <FolderKanban className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                                  {p.name}
                                </span>
                              </SelectItem>
                            ))}
                            
                            {/* Projetos agrupados por setor */}
                            {Object.entries(projectsBySector).map(([sectorId, sectorProjects]) => {
                              const sector = sectors.find(s => s.id === sectorId);
                              return (
                                <div key={sectorId}>
                                  <div className="flex items-center gap-2 text-[10px] font-medium text-slate-400 dark:text-slate-500 py-1 pl-6">
                                    <Layers className="w-3 h-3" />
                                    {sector?.name || 'Setor'}
                                  </div>
                                  {sectorProjects.map(p => (
                                    <SelectItem key={p.id} value={p.id} className="dark:text-slate-200 dark:focus:bg-slate-700 pl-10">
                                      <span className="flex items-center gap-2">
                                        <FolderKanban className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                                        {p.name}
                                      </span>
                                    </SelectItem>
                                  ))}
                                </div>
                              );
                            })}
                          </SelectGroup>
                        );
                      })}
                      
                      {/* Projetos sem departamento */}
                      {projects.filter(p => !p.department_id).length > 0 && (
                        <SelectGroup>
                          <SelectLabel className="flex items-center gap-2 text-xs font-semibold text-slate-500 dark:text-slate-400 py-2">
                            <FolderKanban className="w-3.5 h-3.5" />
                            Outros Projetos
                          </SelectLabel>
                          {projects.filter(p => !p.department_id).map(p => (
                            <SelectItem key={p.id} value={p.id} className="dark:text-slate-200 dark:focus:bg-slate-700 pl-6">
                              <span className="flex items-center gap-2">
                                <FolderKanban className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                                {p.name}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {/* Column Selection - Only show when project is selected */}
                {newTask.project_id && newTask.project_id !== 'personal' && (
                  <div>
                    <label className="text-sm font-medium dark:text-slate-300">Coluna *</label>
                    {loadingColumns ? (
                      <div className="flex items-center gap-2 p-2 text-sm text-slate-500 dark:text-slate-400">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Carregando colunas...
                      </div>
                    ) : (
                      <Select 
                        value={newTask.column_id} 
                        onValueChange={(v) => setNewTask({ ...newTask, column_id: v })}
                      >
                        <SelectTrigger className="dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100">
                          <SelectValue placeholder="Selecione a coluna" />
                        </SelectTrigger>
                        <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                          {(projectColumns[newTask.project_id] || []).map(col => (
                            <SelectItem key={col.id} value={col.id} className="dark:text-slate-200 dark:focus:bg-slate-700">
                              <span className="flex items-center gap-2">
                                <div 
                                  className="w-3 h-3 rounded-full" 
                                  style={{ backgroundColor: col.color }}
                                />
                                {col.name}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium dark:text-slate-300">Descrição</label>
                  <Textarea
                    placeholder="Detalhes da tarefa..."
                    value={newTask.description}
                    onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                    rows={3}
                    className="dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium dark:text-slate-300">Prazo</label>
                    <Input
                      type="date"
                      value={newTask.due_date}
                      onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                      className="dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium dark:text-slate-300">Prioridade</label>
                    <Select value={newTask.priority} onValueChange={(v) => setNewTask({ ...newTask, priority: v })}>
                      <SelectTrigger className="dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                        <SelectItem value="low" className="dark:text-slate-200 dark:focus:bg-slate-700">Baixa</SelectItem>
                        <SelectItem value="medium" className="dark:text-slate-200 dark:focus:bg-slate-700">Média</SelectItem>
                        <SelectItem value="high" className="dark:text-slate-200 dark:focus:bg-slate-700">Alta</SelectItem>
                        <SelectItem value="urgent" className="dark:text-slate-200 dark:focus:bg-slate-700">Urgente</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Recorrência */}
                <div>
                  <label className="text-sm font-medium dark:text-slate-300 flex items-center gap-2">
                    <RefreshCcw className="w-4 h-4" />
                    Recorrência
                  </label>
                  <Select 
                    value={newTask.recorrencia || 'none'} 
                    onValueChange={(v) => setNewTask({ ...newTask, recorrencia: v === 'none' ? null : v })}
                  >
                    <SelectTrigger className="dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100">
                      <SelectValue placeholder="Sem recorrência" />
                    </SelectTrigger>
                    <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                      <SelectItem value="none" className="dark:text-slate-200 dark:focus:bg-slate-700">Sem recorrência</SelectItem>
                      <SelectItem value="diaria" className="dark:text-slate-200 dark:focus:bg-slate-700">Diária</SelectItem>
                      <SelectItem value="semanal" className="dark:text-slate-200 dark:focus:bg-slate-700">Semanal</SelectItem>
                      <SelectItem value="mensal" className="dark:text-slate-200 dark:focus:bg-slate-700">Mensal</SelectItem>
                    </SelectContent>
                  </Select>
                  {newTask.recorrencia && (
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                      Ao concluir, será criada automaticamente a próxima ocorrência
                    </p>
                  )}
                </div>

                {/* Responsável - disponível para todas as tarefas */}
                <div>
                  <label className="text-sm font-medium dark:text-slate-300 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Responsável(is)
                  </label>
                  <div className="border rounded-lg p-2 mt-1 bg-slate-50 dark:bg-slate-900 dark:border-slate-600 max-h-32 overflow-y-auto">
                    {users.length === 0 ? (
                      <p className="text-xs text-slate-500 dark:text-slate-400 text-center py-2">Nenhum usuário disponível</p>
                    ) : (
                      <div className="space-y-1">
                        {users.filter(u => u.role !== 'super_admin').map(u => (
                          <label 
                            key={u.id} 
                            className="flex items-center gap-2 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 p-1.5 rounded text-sm"
                          >
                            <Checkbox
                              checked={newTask.assigned_users.includes(u.id)}
                              onCheckedChange={() => toggleAssignedUser(u.id)}
                            />
                            <User className="w-3.5 h-3.5 text-slate-400" />
                            <span className="dark:text-slate-200">{u.name || u.full_name || u.email}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                  {newTask.assigned_users.length > 0 && (
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                      {newTask.assigned_users.length} responsável(is) selecionado(s)
                    </p>
                  )}
                </div>

                {/* Subtarefas / Checklist */}
                <div>
                  <label className="text-sm font-medium dark:text-slate-300 flex items-center gap-2">
                    <CheckSquare className="w-4 h-4" />
                    Subtarefas
                  </label>
                  <div className="mt-1 space-y-2">
                    {/* Lista de subtarefas adicionadas */}
                    {newTask.checklist.length > 0 && (
                      <div className="border rounded-lg p-2 bg-slate-50 dark:bg-slate-900 dark:border-slate-600 space-y-1">
                        {newTask.checklist.map((subtask, index) => (
                          <div key={subtask.id} className="flex items-center gap-2 group">
                            <Square className="w-4 h-4 text-slate-400" />
                            <span className="flex-1 text-sm dark:text-slate-200">{subtask.text}</span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-600"
                              onClick={() => handleRemoveSubtask(subtask.id)}
                            >
                              <X className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Input para adicionar nova subtarefa */}
                    <div className="flex items-center gap-2">
                      <Input
                        placeholder="Adicionar subtarefa..."
                        value={newSubtask}
                        onChange={(e) => setNewSubtask(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddSubtask())}
                        className="flex-1 h-9 dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleAddSubtask}
                        disabled={!newSubtask.trim()}
                        className="h-9 dark:border-slate-600 dark:text-slate-300"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowNewTaskDialog(false)} className="dark:border-slate-600 dark:text-slate-300">
                  Cancelar
                </Button>
                <Button 
                  onClick={newTask.project_id && newTask.project_id !== 'personal' ? handleCreateTask : handleCreatePersonalTask} 
                  disabled={savingTask || (newTask.project_id && newTask.project_id !== 'personal' && !newTask.column_id)} 
                  data-testid="save-task-btn"
                >
                  {savingTask ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {newTask.project_id && newTask.project_id !== 'personal' ? 'Criar no Projeto' : 'Criar Tarefa Pessoal'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Task Detail Dialog */}
          <Dialog open={showTaskDetailDialog} onOpenChange={setShowTaskDetailDialog}>
            <DialogContent className="max-w-lg dark:bg-slate-800 dark:border-slate-700">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 dark:text-slate-100">
                  <Eye className="w-5 h-5 text-blue-500" />
                  Detalhes da Tarefa
                </DialogTitle>
              </DialogHeader>
              
              {selectedTask && (
                <div className="space-y-4 py-4">
                  {/* Título */}
                  <div>
                    <h3 className={`text-lg font-semibold dark:text-slate-100 ${
                      (selectedTask.status === 'done' || selectedTask.completed_date) ? 'line-through text-slate-400' : ''
                    }`}>
                      {selectedTask.title}
                    </h3>
                  </div>
                  
                  {/* Projeto */}
                  <div className="flex items-center gap-2">
                    <FolderKanban className="w-4 h-4 text-slate-400" />
                    <span className="text-sm text-slate-600 dark:text-slate-400">
                      {selectedTask.project_name || 'Pessoal'}
                    </span>
                  </div>
                  
                  {/* Status e Prioridade */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={`${getStatusInfo(selectedTask.status || 'todo').color}`}>
                      {getStatusInfo(selectedTask.status || 'todo').label}
                    </Badge>
                    <Badge className={`${getPriorityInfo(selectedTask.priority || 'medium').color}`}>
                      {getPriorityInfo(selectedTask.priority || 'medium').label}
                    </Badge>
                    {selectedTask.flag && (
                      <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                        <Flag className="w-3 h-3 mr-1" />
                        {selectedTask.flag === 'proxima_acao' ? 'Próxima Ação' :
                         selectedTask.flag === 'aguardando_retorno' ? 'Aguardando Retorno' : 'Revisar'}
                      </Badge>
                    )}
                  </div>
                  
                  {/* Descrição */}
                  {selectedTask.description && (
                    <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                      <p className="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-wrap">
                        {selectedTask.description}
                      </p>
                    </div>
                  )}
                  
                  {/* Responsáveis */}
                  {selectedTask.assigned_user_names?.length > 0 && (
                    <div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Responsáveis:</p>
                      <div className="flex flex-wrap gap-1">
                        {selectedTask.assigned_user_names.map((name, i) => (
                          <Badge key={i} variant="outline" className="text-xs dark:border-slate-600 dark:text-slate-300">
                            <User className="w-3 h-3 mr-1" />
                            {name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Data de Vencimento */}
                  {selectedTask.due_date && (
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      <span className="text-sm text-slate-600 dark:text-slate-300">
                        Vencimento: {format(new Date(selectedTask.due_date), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                      </span>
                    </div>
                  )}
                  
                  {/* Checklist */}
                  {selectedTask.checklist?.length > 0 && (
                    <div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                        Checklist ({selectedTask.checklist.filter(c => c.completed).length}/{selectedTask.checklist.length}):
                      </p>
                      <div className="space-y-1">
                        {selectedTask.checklist.map((item, i) => (
                          <div key={i} className="flex items-center gap-2">
                            {item.completed ? (
                              <CheckCircle2 className="w-4 h-4 text-green-500" />
                            ) : (
                              <Circle className="w-4 h-4 text-slate-300 dark:text-slate-600" />
                            )}
                            <span className={`text-sm ${item.completed ? 'line-through text-slate-400' : 'dark:text-slate-300'}`}>
                              {item.text}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Comentários */}
                  {selectedTask.comments?.length > 0 && (
                    <div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                        Comentários ({selectedTask.comments.length}):
                      </p>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {selectedTask.comments.map((comment, i) => (
                          <div key={i} className="p-2 bg-slate-50 dark:bg-slate-900 rounded text-sm">
                            <p className="font-medium text-slate-700 dark:text-slate-300">{comment.user_name}</p>
                            <p className="text-slate-600 dark:text-slate-400">{comment.text}</p>
                            <p className="text-xs text-slate-400 mt-1">
                              {format(new Date(comment.created_at), "dd/MM/yy HH:mm")}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              <DialogFooter className="flex gap-2">
                {selectedTask && !selectedTask.is_personal && (
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowTaskDetailDialog(false);
                      navigate(`/projects/${selectedTask.project_id}/kanban`);
                    }}
                    className="dark:border-slate-600"
                  >
                    <FolderKanban className="w-4 h-4 mr-2" />
                    Ir para Kanban
                  </Button>
                )}
                <Button onClick={() => setShowTaskDetailDialog(false)}>
                  Fechar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* View Mode Toggle */}
          <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-800 p-1 rounded-lg">
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className="gap-1"
            >
              <ListTodo className="w-4 h-4" />
              Lista
            </Button>
            <Button
              variant={viewMode === 'table' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('table')}
              className="gap-1"
            >
              <TableIcon className="w-4 h-4" />
              Tabela
            </Button>
            <Button
              variant={viewMode === 'board' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('board')}
              className="gap-1"
            >
              <LayoutGrid className="w-4 h-4" />
              Quadro
            </Button>
            <Button
              variant={viewMode === 'timeline' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('timeline')}
              className="gap-1"
            >
              <Calendar className="w-4 h-4" />
              Timeline
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="dark:bg-slate-800/50 dark:border-slate-700">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
              <Input
                placeholder="Buscar tarefas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100"
              />
            </div>
            
            <Select value={projectFilter} onValueChange={setProjectFilter}>
              <SelectTrigger className="w-full md:w-48 dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100">
                <FolderKanban className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Projeto" />
              </SelectTrigger>
              <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                <SelectItem value="all" className="dark:text-slate-200 dark:focus:bg-slate-700">Todos os Projetos</SelectItem>
                <SelectItem value="personal" className="dark:text-slate-200 dark:focus:bg-slate-700">
                  <span className="flex items-center gap-2">
                    <UserCircle className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    Tarefas Pessoais
                  </span>
                </SelectItem>
                {projects.map(p => (
                  <SelectItem key={p.id} value={p.id} className="dark:text-slate-200 dark:focus:bg-slate-700">{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-40 dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                <SelectItem value="all" className="dark:text-slate-200 dark:focus:bg-slate-700">Todos Status</SelectItem>
                <SelectItem value="todo" className="dark:text-slate-200 dark:focus:bg-slate-700">A Fazer</SelectItem>
                <SelectItem value="doing" className="dark:text-slate-200 dark:focus:bg-slate-700">Fazendo</SelectItem>
                <SelectItem value="review" className="dark:text-slate-200 dark:focus:bg-slate-700">Revisão</SelectItem>
                <SelectItem value="done" className="dark:text-slate-200 dark:focus:bg-slate-700">Concluído</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* List View */}
      {viewMode === 'list' && (
        <Card className="dark:bg-slate-800/50 dark:border-slate-700">
          <CardContent className="pt-6">
            {sortedTasks.length === 0 ? (
              <div className="text-center py-12">
                <ListTodo className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600 mb-3" />
                <p className="text-slate-500 dark:text-slate-400">Nenhuma tarefa encontrada</p>
              </div>
            ) : (
              <div className="space-y-2">
                {sortedTasks.map(task => {
                  const status = getStatusInfo(task.status);
                  const priority = getPriorityInfo(task.priority);
                  const dueInfo = getDueDateInfo(task.due_date);
                  const StatusIcon = status.icon;
                  const isCompleted = task.is_personal ? !!task.completed_date : task.status === 'done';
                  
                  return (
                    <div 
                      key={task.id}
                      className="flex items-center gap-3 p-3 border rounded-lg hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-700/50 cursor-pointer transition-colors"
                      onClick={() => handleOpenTaskDetail(task)}
                    >
                      {task.is_personal ? (
                        <Checkbox
                          checked={isCompleted}
                          onCheckedChange={(e) => handleTogglePersonalTaskComplete(task, { stopPropagation: () => {} })}
                          onClick={(e) => e.stopPropagation()}
                          className="data-[state=checked]:bg-green-500"
                        />
                      ) : (
                        <StatusIcon className={`w-5 h-5 ${isCompleted ? 'text-green-500' : 'text-slate-400 dark:text-slate-500'}`} />
                      )}
                      
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium truncate dark:text-slate-200 ${isCompleted ? 'line-through text-slate-400 dark:text-slate-500' : ''}`}>
                          {task.title}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge 
                            variant="outline" 
                            className={`text-xs dark:border-slate-600 ${task.is_personal ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-700' : 'dark:text-slate-400'}`}
                          >
                            {task.is_personal && <UserCircle className="w-3 h-3 mr-1" />}
                            {task.project_name}
                          </Badge>
                          {task.assigned_user_names?.length > 0 && (
                            <>
                              <span className="text-slate-300 dark:text-slate-600">•</span>
                              <span className="text-xs text-blue-600 dark:text-blue-400 flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {task.assigned_user_names.join(', ')}
                              </span>
                            </>
                          )}
                          {task.description && (
                            <>
                              <span className="text-slate-300">•</span>
                              <span className="text-xs text-slate-400 truncate max-w-[200px]">{task.description}</span>
                            </>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {/* Flag Dropdown */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant="ghost"
                              size="icon"
                              className={`h-7 w-7 ${task.flag ? 'text-amber-500' : 'text-slate-400 hover:text-slate-600'}`}
                            >
                              <Flag className="w-3.5 h-3.5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48 dark:bg-slate-800 dark:border-slate-700">
                            <DropdownMenuItem 
                              onClick={(e) => handleUpdateTaskFlag(task.id, 'proxima_acao', e)}
                              className={`gap-2 dark:text-slate-200 ${task.flag === 'proxima_acao' ? 'bg-green-50 dark:bg-green-900/30' : ''}`}
                            >
                              <PlayCircle className="w-4 h-4 text-green-600" />
                              Próxima Ação
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={(e) => handleUpdateTaskFlag(task.id, 'aguardando_retorno', e)}
                              className={`gap-2 dark:text-slate-200 ${task.flag === 'aguardando_retorno' ? 'bg-amber-50 dark:bg-amber-900/30' : ''}`}
                            >
                              <Clock className="w-4 h-4 text-amber-600" />
                              Aguardando Retorno
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={(e) => handleUpdateTaskFlag(task.id, 'revisar', e)}
                              className={`gap-2 dark:text-slate-200 ${task.flag === 'revisar' ? 'bg-blue-50 dark:bg-blue-900/30' : ''}`}
                            >
                              <Eye className="w-4 h-4 text-blue-600" />
                              Revisar
                            </DropdownMenuItem>
                            {task.flag && (
                              <>
                                <DropdownMenuSeparator className="dark:bg-slate-700" />
                                <DropdownMenuItem 
                                  onClick={(e) => handleUpdateTaskFlag(task.id, null, e)}
                                  className="gap-2 text-slate-500 dark:text-slate-400"
                                >
                                  <RotateCcw className="w-4 h-4" />
                                  Remover Flag
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                        {/* Flag Badge */}
                        {task.flag && (() => {
                          const flagInfo = getFlagInfo(task.flag);
                          const FlagIcon = flagInfo?.icon;
                          return flagInfo ? (
                            <Badge className={`text-[10px] px-1.5 py-0.5 ${flagInfo.color}`}>
                              <FlagIcon className="w-3 h-3 mr-0.5" />
                              {flagInfo.label}
                            </Badge>
                          ) : null;
                        })()}
                        {dueInfo && (
                          <Badge variant="outline" className={`text-xs ${dueInfo.color}`}>
                            {dueInfo.label}
                          </Badge>
                        )}
                        {/* Checklist indicator */}
                        {task.checklist?.length > 0 && (
                          <Badge variant="outline" className="text-xs text-slate-500 dark:text-slate-400 dark:border-slate-600">
                            <CheckSquare className="w-3 h-3 mr-1" />
                            {task.checklist.filter(c => c.completed).length}/{task.checklist.length}
                          </Badge>
                        )}
                        <Badge className={`text-xs ${priority.color}`}>
                          {priority.label}
                        </Badge>
                        {task.is_personal ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-slate-400 hover:text-red-500"
                            onClick={(e) => handleDeletePersonalTask(task.id, e)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        ) : (
                          <ChevronRight className="w-4 h-4 text-slate-400 dark:text-slate-500" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <Card className="dark:bg-slate-800/50 dark:border-slate-700">
          <CardContent className="pt-6">
            <ScrollArea className="h-[500px]">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 dark:bg-slate-800">
                    <TableHead className="dark:text-slate-300">Tarefa</TableHead>
                    <TableHead className="dark:text-slate-300">Projeto</TableHead>
                    <TableHead className="dark:text-slate-300">Status</TableHead>
                    <TableHead className="dark:text-slate-300">Prioridade</TableHead>
                    <TableHead className="dark:text-slate-300">Flag</TableHead>
                    <TableHead className="dark:text-slate-300">Subtarefas</TableHead>
                    <TableHead className="dark:text-slate-300">Prazo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedTasks.map(task => {
                    const status = getStatusInfo(task.status);
                    const priority = getPriorityInfo(task.priority);
                    const dueInfo = getDueDateInfo(task.due_date);
                    
                    return (
                      <TableRow 
                        key={task.id}
                        className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50"
                        onClick={() => navigate(`/projects/${task.project_id}/kanban`)}
                      >
                        <TableCell>
                          <p className={`font-medium dark:text-slate-200 ${task.status === 'done' ? 'line-through text-slate-400 dark:text-slate-500' : ''}`}>
                            {task.title}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            {task.assigned_user_names?.length > 0 && (
                              <span className="text-xs text-blue-600 dark:text-blue-400 flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {task.assigned_user_names.join(', ')}
                              </span>
                            )}
                            {task.description && (
                              <>
                                {task.assigned_user_names?.length > 0 && <span className="text-slate-300 dark:text-slate-600">•</span>}
                                <span className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-xs">{task.description}</span>
                              </>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs dark:border-slate-600 dark:text-slate-300">{task.project_name}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={`text-xs ${status.color}`}>{status.label}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={`text-xs ${priority.color}`}>{priority.label}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className={`h-6 w-6 ${task.flag ? 'text-amber-500' : 'text-slate-400 hover:text-slate-600'}`}
                                >
                                  <Flag className="w-3 h-3" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="start" className="w-44 dark:bg-slate-800 dark:border-slate-700">
                                <DropdownMenuItem 
                                  onClick={(e) => handleUpdateTaskFlag(task.id, 'proxima_acao', e)}
                                  className={`gap-2 text-sm dark:text-slate-200 ${task.flag === 'proxima_acao' ? 'bg-green-50 dark:bg-green-900/30' : ''}`}
                                >
                                  <PlayCircle className="w-3.5 h-3.5 text-green-600" />
                                  Próxima Ação
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={(e) => handleUpdateTaskFlag(task.id, 'aguardando_retorno', e)}
                                  className={`gap-2 text-sm dark:text-slate-200 ${task.flag === 'aguardando_retorno' ? 'bg-amber-50 dark:bg-amber-900/30' : ''}`}
                                >
                                  <Clock className="w-3.5 h-3.5 text-amber-600" />
                                  Aguardando
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={(e) => handleUpdateTaskFlag(task.id, 'revisar', e)}
                                  className={`gap-2 text-sm dark:text-slate-200 ${task.flag === 'revisar' ? 'bg-blue-50 dark:bg-blue-900/30' : ''}`}
                                >
                                  <Eye className="w-3.5 h-3.5 text-blue-600" />
                                  Revisar
                                </DropdownMenuItem>
                                {task.flag && (
                                  <>
                                    <DropdownMenuSeparator className="dark:bg-slate-700" />
                                    <DropdownMenuItem 
                                      onClick={(e) => handleUpdateTaskFlag(task.id, null, e)}
                                      className="gap-2 text-sm text-slate-500 dark:text-slate-400"
                                    >
                                      <RotateCcw className="w-3.5 h-3.5" />
                                      Remover
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                            {task.flag && (() => {
                              const flagInfo = getFlagInfo(task.flag);
                              const FlagIcon = flagInfo?.icon;
                              return flagInfo ? (
                                <Badge className={`text-[10px] px-1 py-0 ${flagInfo.color}`}>
                                  <FlagIcon className="w-2.5 h-2.5" />
                                </Badge>
                              ) : null;
                            })()}
                          </div>
                        </TableCell>
                        <TableCell>
                          {task.checklist?.length > 0 ? (
                            <span className="text-xs text-slate-600 dark:text-slate-400 flex items-center gap-1">
                              <CheckSquare className="w-3 h-3" />
                              {task.checklist.filter(c => c.completed).length}/{task.checklist.length}
                            </span>
                          ) : (
                            <span className="text-slate-400 dark:text-slate-600">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {dueInfo ? (
                            <Badge variant="outline" className={`text-xs ${dueInfo.color}`}>
                              {dueInfo.label}
                            </Badge>
                          ) : '-'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Board View (Mini Kanban) */}
      {viewMode === 'board' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Object.entries(tasksByStatus).map(([status, statusTasks]) => {
            const info = getStatusInfo(status);
            const Icon = info.icon;
            
            return (
              <Card key={status} className="dark:bg-slate-800/50 dark:border-slate-700">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2 dark:text-slate-200">
                    <Icon className="w-4 h-4" />
                    {info.label}
                    <Badge variant="secondary" className="ml-auto">{statusTasks.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[300px]">
                    <div className="space-y-2">
                      {statusTasks.length === 0 ? (
                        <p className="text-center text-sm text-slate-400 dark:text-slate-500 py-4">Vazio</p>
                      ) : (
                        statusTasks.map(task => {
                          const priority = getPriorityInfo(task.priority);
                          const dueInfo = getDueDateInfo(task.due_date);
                          
                          return (
                            <div 
                              key={task.id}
                              className="p-2 border rounded bg-white dark:bg-slate-800 dark:border-slate-600 hover:shadow-sm cursor-pointer transition-shadow"
                              onClick={() => navigate(`/projects/${task.project_id}/kanban`)}
                            >
                              <p className="text-sm font-medium truncate dark:text-slate-200">{task.title}</p>
                              <div className="flex items-center gap-1 mt-1 flex-wrap">
                                <Badge variant="outline" className="text-[10px] dark:border-slate-600 dark:text-slate-400">{task.project_name}</Badge>
                                {task.checklist?.length > 0 && (
                                  <Badge variant="outline" className="text-[10px] dark:border-slate-600 dark:text-slate-400">
                                    <CheckSquare className="w-2.5 h-2.5 mr-0.5" />
                                    {task.checklist.filter(c => c.completed).length}/{task.checklist.length}
                                  </Badge>
                                )}
                                {dueInfo && (
                                  <Badge variant="outline" className={`text-[10px] ${dueInfo.color}`}>
                                    {dueInfo.label}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Timeline View */}
      {viewMode === 'timeline' && (
        <Card className="dark:bg-slate-800/50 dark:border-slate-700">
          <CardContent className="pt-6">
            {Object.keys(tasksByDate).length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600 mb-3" />
                <p className="text-slate-500 dark:text-slate-400">Nenhuma tarefa encontrada</p>
              </div>
            ) : (
              <div className="space-y-6">
                {Object.entries(tasksByDate)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([date, dateTasks]) => {
                    const isNoDate = date === 'sem-data';
                    const dateObj = isNoDate ? null : new Date(date);
                    
                    return (
                      <div key={date} className="relative pl-8">
                        {/* Timeline line */}
                        <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-slate-200 dark:bg-slate-700" />
                        
                        {/* Date marker */}
                        <div className="absolute left-0 w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center">
                          <Calendar className="w-3 h-3 text-white" />
                        </div>
                        
                        {/* Date header */}
                        <div className="mb-3">
                          {isNoDate ? (
                            <p className="font-semibold text-slate-500 dark:text-slate-400">Sem data definida</p>
                          ) : (
                            <>
                              <p className="font-semibold dark:text-slate-200">
                                {format(dateObj, "EEEE, dd 'de' MMMM", { locale: ptBR })}
                              </p>
                              <p className="text-xs text-slate-500 dark:text-slate-400">
                                {isToday(dateObj) && '(Hoje)'}
                                {isTomorrow(dateObj) && '(Amanhã)'}
                                {isPast(dateObj) && !isToday(dateObj) && '(Atrasada)'}
                              </p>
                            </>
                          )}
                        </div>
                        
                        {/* Tasks for this date */}
                        <div className="space-y-2">
                          {dateTasks.map(task => {
                            const status = getStatusInfo(task.status);
                            const StatusIcon = status.icon;
                            
                            return (
                              <div 
                                key={task.id}
                                className="flex items-center gap-3 p-3 border rounded-lg bg-white dark:bg-slate-800 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer"
                                onClick={() => navigate(`/projects/${task.project_id}/kanban`)}
                              >
                                <StatusIcon className={`w-4 h-4 ${task.status === 'done' ? 'text-green-500' : 'text-slate-400 dark:text-slate-500'}`} />
                                <div className="flex-1">
                                  <p className={`font-medium dark:text-slate-200 ${task.status === 'done' ? 'line-through text-slate-400 dark:text-slate-500' : ''}`}>
                                    {task.title}
                                  </p>
                                  <p className="text-xs text-slate-500 dark:text-slate-400">{task.project_name}</p>
                                </div>
                                <Badge className={`text-xs ${status.color}`}>{status.label}</Badge>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TasksPage;
